import { useEffect, useMemo, type FC } from 'react';

import { Drawer, FormControlLabel, Grid, Stack, TextField, Typography } from '@mui/material';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';

import { Button, Checkbox, DrawerContainer } from '../../../components/base';
import { AutocompleteController } from '../../../components/form/Autocomplete';
import { useFullScreen } from '../../../components/FullScreen';
import {
  ResolutionLevelRenderer,
  PhotosController,
  SelectController,
  StatusRenderer,
  UserRenderer,
} from '../../../components/IncidentForm/components';
import EndDatetimeController from '../../../components/IncidentForm/components/EndDatetimeController';
import { MultiUsersController } from '../../../components/IncidentForm/components/MultiUsersController';
import { AVAILABLE_STATUSES } from '../../../constants/base';
import { useConfirmDialog } from '../../../hooks/useConfirmDialog';
import { locationsState } from '../../../state/locations';
import { userFunctionsState } from '../../../state/userFunctions';
import type { User } from '../../../types';
import { ResolutionLevel, Status } from '../../../types';
import getFullName from '../../../utils/getFullName';
import { usersState } from '../../users';
import type { AiIncident, AiIncidentForm } from '../model/types';

type AiIncidentConfirmDialogProps = {
  aiIncident?: AiIncident;
  isConfirmLoading?: boolean;
  open: boolean;
  onClose: () => void;
  onConfirm?: (aiIncident: AiIncident, values: AiIncidentForm) => Promise<void> | void;
};

const findUserByAiIncidentUser = (users: User[], aiIncidentUser?: User | null) => {
  if (!aiIncidentUser) {
    return null;
  }

  return users.find((user) => user.id === aiIncidentUser.id) ?? null;
};

const getInitialFormValues = (aiIncident: AiIncident, users: User[]): AiIncidentForm => ({
  title: aiIncident.title,
  description: aiIncident.description,
  status: aiIncident.status,
  resolutionLevel: aiIncident.resolutionLevel,
  responsible: findUserByAiIncidentUser(users, aiIncident.responsible),
  control: findUserByAiIncidentUser(users, aiIncident.control),
  participants: [],
  userFunction: aiIncident.userFunction,
  location: aiIncident.location,
  locationDescription: '',
  endDatetime: null,
  isResponsibleInformed: false,
  isUrgent: false,
  photoSources: [],
});

export const AiIncidentConfirmDialog: FC<AiIncidentConfirmDialogProps> = ({
  aiIncident,
  isConfirmLoading,
  open,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const { ref } = useFullScreen();
  const users = useRecoilValue(usersState);
  const locations = useRecoilValue(locationsState);
  const userFunctions = useRecoilValue(userFunctionsState);
  const initialFormValues = useMemo(
    () => (aiIncident ? getInitialFormValues(aiIncident, users) : undefined),
    [aiIncident, users],
  );
  const form = useForm<AiIncidentForm>({
    defaultValues: initialFormValues,
  });
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = form;
  const { showConfirm, confirmModal } = useConfirmDialog({
    title: t('notifications.exit.title'),
    content: t('notifications.exit.content'),
  });

  useEffect(() => {
    if (initialFormValues) {
      form.reset(initialFormValues);
    }
  }, [form, initialFormValues]);

  const handleClose = async () => {
    if (isDirty) {
      const isConfirmed = await showConfirm();

      if (!isConfirmed) {
        return;
      }
    }

    onClose();
  };

  const onSubmit = async (formValues: AiIncidentForm) => {
    if (!aiIncident) {
      return;
    }

    await onConfirm?.(aiIncident, formValues);
    onClose();
  };

  if (!aiIncident || !initialFormValues) {
    return null;
  }

  return (
    <>
      <Drawer container={ref.current} anchor='right' open={open} onClose={handleClose}>
        <FormProvider {...form}>
          <DrawerContainer component='form' onSubmit={handleSubmit(onSubmit)}>
            <Stack>
              <Typography variant='h4' sx={{ marginBottom: '16px' }}>
                {t('confirm-incident')}
              </Typography>
              <Grid container columnSpacing={1} rowSpacing={3} alignItems='center'>
                <Grid xs={12} item>
                  <Controller
                    name='title'
                    rules={{ required: true, validate: (value) => Boolean((value || '').trim()) }}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        error={Boolean(errors.title)}
                        placeholder={t('title')}
                        label={t('title')}
                        fullWidth
                        required
                        inputProps={{
                          maxLength: 250,
                        }}
                        {...field}
                      />
                    )}
                  />
                </Grid>

                <Grid xs={12} item>
                  <Controller
                    name='description'
                    control={control}
                    render={({ field }) => (
                      <TextField
                        placeholder={t('description')}
                        label={t('description')}
                        fullWidth
                        maxRows={5}
                        multiline
                        {...field}
                      />
                    )}
                  />
                </Grid>

                <Grid xs={12} sm={3} item>
                  <SelectController
                    name='status'
                    label={t('status')}
                    required
                    data={AVAILABLE_STATUSES[Status.REGISTERED]}
                    itemRenderer={StatusRenderer}
                  />
                </Grid>

                <Grid xs={12} sm={3} item>
                  <SelectController
                    name='resolutionLevel'
                    label={t('level')}
                    required
                    data={Object.values(ResolutionLevel)}
                    itemRenderer={ResolutionLevelRenderer}
                  />
                </Grid>

                <Grid xs={12} sm={6} item>
                  <AutocompleteController
                    name='responsible'
                    label={t('responsible')}
                    placeholder={t('responsible')}
                    options={users}
                    getOptionLabel={(user) => getFullName(user)}
                    renderOption={(props, user) => <li {...props}>{UserRenderer(user)}</li>}
                  />
                </Grid>

                <Grid xs={12} item>
                  <AutocompleteController
                    name='control'
                    label={t('control')}
                    placeholder={t('control')}
                    options={users}
                    getOptionLabel={(user) => getFullName(user)}
                    renderOption={(props, user) => <li {...props}>{UserRenderer(user)}</li>}
                  />
                </Grid>

                <Grid xs={12} item>
                  <MultiUsersController name='participants' label={t('participants')} options={users} />
                </Grid>

                <Grid xs={12} item>
                  <AutocompleteController
                    name='userFunction'
                    label={t('user-function')}
                    placeholder={t('user-function')}
                    options={userFunctions}
                    getOptionLabel={(userFunction) => userFunction.name}
                    renderOption={(props, userFunction) => <li {...props}>{userFunction.name}</li>}
                  />
                </Grid>

                <Grid xs={12} item>
                  <AutocompleteController
                    name='location'
                    label={t('location')}
                    placeholder={t('location')}
                    options={locations}
                    getOptionLabel={(location) => location.title}
                    renderOption={(props, location) => <li {...props}>{location.title}</li>}
                  />
                </Grid>

                <Grid xs={12} item>
                  <Controller
                    name='locationDescription'
                    control={control}
                    render={({ field }) => (
                      <TextField
                        placeholder={t('location-details')}
                        label={t('location-details')}
                        fullWidth
                        maxRows={5}
                        multiline
                        {...field}
                      />
                    )}
                  />
                </Grid>

                <Grid xs={12} item>
                  <EndDatetimeController name='endDatetime' label={t('end-datetime')} />
                </Grid>

                <Grid xs={12} item>
                  <Controller
                    name='isResponsibleInformed'
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Checkbox name='isResponsibleInformed' />}
                        label={t('responsible-informed')}
                        {...field}
                      />
                    )}
                  />
                </Grid>

                <Grid xs={12} item>
                  <Controller
                    name='isUrgent'
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel control={<Checkbox name='isUrgent' />} label={t('urgent')} {...field} />
                    )}
                  />
                </Grid>

                <Grid xs={12} item>
                  <PhotosController />
                </Grid>
              </Grid>
            </Stack>
            <Button
              fullWidth
              disabled={Object.keys(errors).length > 0}
              loading={isConfirmLoading}
              type='submit'
              sx={{ marginTop: 'auto', color: 'white' }}
              variant='contained'
            >
              {t('confirm')}
            </Button>
          </DrawerContainer>
        </FormProvider>
      </Drawer>
      {confirmModal}
    </>
  );
};
